import { BasicState } from "@app/geography";
import { BenchmarkingDashboardDetails, BenchmarkingProviderFlyoutData } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { BenchmarkingFilterData } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { QHPContractPrimaryFiltersPayload, QHPNetwork, QHPNetworkFilterData, QHPPrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters";
import { NetworkComparisonFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared";
import { EffectivenessAndEfficiencyData, HospitalAndPcpCountData, ProviderOverlapData, ProvidersByNetworkData, TotalProviderCountData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { ServiceBase } from "@app/shared";

/** handles communication across the wire when interacting with the network benchmarking dashboard */
export default class BenchmarkingQHPService extends ServiceBase {
  private benchmarkingQHPEndpoint = `${this.apiEndpoint}/benchmarkingmarketplace`;

  public async getBenchmarkingPrimaryFilterData(clientId: number): Promise<BenchmarkingFilterData> {
    return await this.makeGet<BenchmarkingFilterData>(`${clientId}/primary-filters`, null, this.benchmarkingQHPEndpoint);
  }

  public async getBenchmarkingNetworkData(primaryFilters: QHPPrimaryFiltersPayload): Promise<QHPNetworkFilterData> {
    if (!primaryFilters) {
      return null;
    }

    return await this.makePost<QHPNetworkFilterData>(`get-networks`, primaryFilters, this.benchmarkingQHPEndpoint);
  }

  public async getContractDetails(primaryFilters: QHPContractPrimaryFiltersPayload): Promise<QHPNetwork> {
    if (!primaryFilters) {
      return null;
    }

    return await this.makePost<QHPNetwork>(`contract`, primaryFilters, this.benchmarkingQHPEndpoint);
  }

  public async getTotalProviderCountData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): Promise<TotalProviderCountData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<TotalProviderCountData[]>(`total-provider-count`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint);
  }

  public async getHospitalAndPcpCountData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): Promise<HospitalAndPcpCountData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<HospitalAndPcpCountData[]>(`hospital-and-pcp-count`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint);
  }

  public async getProvidersByNetworkData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): Promise<ProvidersByNetworkData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<ProvidersByNetworkData[]>(`providers-by-network`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint);
  }

  public async getEffectivenessAndEfficiencyData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): Promise<EffectivenessAndEfficiencyData[]> {
    if (!networkComparisonFiltersPayload) {
      return [];
    }

    return await this.makePost<EffectivenessAndEfficiencyData[]>(`effectiveness-and-efficiency`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint);
  }

  public async getProviderOverlapData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): Promise<ProviderOverlapData> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<ProviderOverlapData>(`provider-overlap`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint);
  }

  public async getDashboardDetailsData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): Promise<BenchmarkingDashboardDetails[]> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<BenchmarkingDashboardDetails[]>(`dashboard-details`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint);
  }

  public async getProviderFlyoutData(networkComparisonFiltersPayload: NetworkComparisonFiltersPayload): Promise<BenchmarkingProviderFlyoutData> {
    if (!networkComparisonFiltersPayload) {
      return null;
    }

    return await this.makePost<BenchmarkingProviderFlyoutData>(`provider-details`, networkComparisonFiltersPayload, this.benchmarkingQHPEndpoint);
  }

  public async getQhpStates(): Promise<BasicState[]> {
    return await this.makeGet<BasicState[]>(`get-qhp-states`, null, this.benchmarkingQHPEndpoint);
  }
}
